/** Make pasted and dropped pictures ours before they reach the article.
 *
 * Without this, a paste from Helpjuice keeps `static.helpjuice.com` in the
 * `src` (a hotlink that dies with their account), a clipboard screenshot is
 * dropped on the floor, and a Word/Docs paste embeds a base64 blob in the
 * article body. Each image is uploaded — or re-hosted server-side, through the
 * SSRF guard — and only the resulting `/api/kb/assets/<id>` URL is inserted.
 */
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

/** `manage-admins-3.png` — the article it was pasted into and which picture it
 *  is. A clipboard screenshot arrives from the browser called "image.png", so
 *  without this the media library fills with hundreds of identical names and
 *  nobody can ever find one again to reuse. */
export const kbImageName = (n, type = "image/png") => {
  const slug = (window.location.pathname.match(/\/kb\/(?:a|c)\/([\w-]+)/) || [])[1] || "kb";
  const ext = (String(type).split("/")[1] || "png").replace("jpeg", "jpg");
  return `${slug}-${n}.${ext}`;
};

const countImages = (editor) => ((editor?.getHTML() || "").match(/<img\b/gi) || []).length;

const uploadFile = async (file, name) => {
  const fd = new FormData();
  fd.append("file", file, name || file.name || "pasted.png");
  const { data } = await api.post("/kb/upload", fd,
    { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

const rehostRemote = async (url) => {
  const { data } = await api.post("/kb/upload-from-url", { url });
  return data.url;
};

const dataUrlToFile = async (dataUrl, name) => {
  const blob = await (await fetch(dataUrl)).blob();
  const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
  return new File([blob], `${name}.${ext}`, { type: blob.type });
};

const imageFilesFrom = (list) =>
  [...(list || [])].filter((f) => IMAGE_TYPES.includes((f.type || "").toLowerCase()));

/** Upload each file and insert them as images. */
async function insertFiles(editor, files) {
  const t = toast.loading(files.length > 1
    ? `Saving ${files.length} images to our storage…` : "Saving the image to our storage…");
  let saved = 0;
  let n = countImages(editor);
  for (const file of files) {
    try {
      n += 1;
      const url = await uploadFile(file, kbImageName(n, file.type));
      editor.chain().focus().setImage({ src: url }).run();
      saved += 1;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That image wouldn't upload");
    }
  }
  if (saved) {
    toast.success(saved > 1 ? `${saved} images saved to our storage` : "Image saved to our storage",
      { id: t });
  } else {
    toast.dismiss(t);
  }
}

/** Rewrite every <img> in pasted HTML to a URL of our own, then insert it. */
async function insertRehostedHtml(editor, html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const imgs = [...doc.querySelectorAll("img")];
  const t = toast.loading(imgs.length > 1
    ? `Saving ${imgs.length} images to our storage…` : "Saving the image to our storage…");
  let saved = 0;
  let kept = 0;
  let n = countImages(editor);
  for (const img of imgs) {
    const src = (img.getAttribute("src") || "").trim();
    if (!src || src.startsWith("/api/kb/assets/")) continue;
    try {
      if (src.startsWith("data:image/")) {
        n += 1;
        const file = await dataUrlToFile(src, "pasted");
        img.setAttribute("src", await uploadFile(file, kbImageName(n, file.type)));
      } else if (/^https?:\/\//i.test(src)) {
        img.setAttribute("src", await rehostRemote(src));
      } else {
        continue;
      }
      saved += 1;
    } catch (e) {
      // Keep the original so the author still sees their picture — it just
      // stays a hotlink until Image Rescue picks it up.
      kept += 1;
      console.warn("Could not re-host a pasted image:", src, e);
    }
  }
  editor.chain().focus().insertContent(doc.body.innerHTML).run();
  if (saved) {
    toast.success(saved > 1 ? `${saved} images saved to our storage` : "Image saved to our storage",
      { id: t });
  } else {
    toast.dismiss(t);
  }
  if (kept) {
    toast.warning(`${kept} image${kept === 1 ? "" : "s"} stayed on the original site — `
      + "run Image hosting in KB settings to bring them across");
  }
}

/** ProseMirror `handlePaste`. Returns true when we've taken over. */
export function pasteImages(editor, event) {
  if (!editor) return false;
  const files = imageFilesFrom(event.clipboardData?.files);
  if (files.length) {
    event.preventDefault();
    insertFiles(editor, files);
    return true;
  }
  const html = event.clipboardData?.getData("text/html") || "";
  if (html && /<img\b/i.test(html)) {
    event.preventDefault();
    insertRehostedHtml(editor, html);
    return true;
  }
  return false;
}

/** ProseMirror `handleDrop`. */
export function dropImages(editor, event) {
  if (!editor) return false;
  const files = imageFilesFrom(event.dataTransfer?.files);
  if (!files.length) return false;
  event.preventDefault();
  insertFiles(editor, files);
  return true;
}
